import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { AirportConfig } from "@yb/core";
import { api, type BagRanked, type BagsFlightRow, type BagsHistory } from "../lib/api";
import { AirlineMark, Direction } from "./AirlineMark";
import { HowToRead } from "./HowToRead";
import { Section } from "./Section";

/**
 * The History page's Bags tab: what the bag system counted over the range, today unless
 * the reader picks other dates. The figure for each flight is the furthest point down the
 * line that reported, the same figure the By flight tab shows.
 *
 * A connecting bag goes on past the flight: tagged beyond a departure's destination, or on
 * to another flight here. Direct and connecting are only split where the bag system said,
 * by giving bags by final destination or a connecting count. Until it does, those figures
 * read "not reported" rather than showing every bag as direct.
 */

const card = "rounded-xl border border-line p-4";
const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1.5 align-middle";
const num = (v: number | null | undefined) => (v == null ? "—" : v.toLocaleString());
const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;
const NOT_REPORTED = "not reported";
const NO_FINALS = "The bag system does not send final destinations yet.";

/**
 * Why direct and connecting are empty, and what would fill them, said once where the reader
 * is looking rather than left as a column of blanks. Field names the bag system sends that
 * are not used yet are listed: a final destination may already be arriving under another name.
 */
function NoDestinations({ h }: { h: BagsHistory }) {
  return (
    <div className="mb-3 max-w-3xl rounded-lg border border-warn/40 bg-warn/5 px-3 py-2 text-[12.5px] text-ink-2">
      <p>
        No flight in this range came with its bags by final destination, so direct and connecting bags cannot be told apart yet. VectroLake can send them with each count as{" "}
        <code className="text-[11.5px]">destinations</code>: the flight&apos;s bags grouped by the airport each is tagged to, from the bag tags it already reads.
      </p>
      {h.unusedFields.length > 0 && (
        <p className="mt-1">
          The bag system also sends {h.unusedFields.map((f, i) => (
            <span key={f}>
              {i > 0 && ", "}
              <code className="text-[11.5px]">{f}</code>
            </span>
          ))}
          , not used yet.
        </p>
      )}
    </div>
  );
}

/** The tab's opening line. */
export function bagsSentence(h: BagsHistory | null, place: string, span: { from: string; to: string; today: string }, tz: string): string {
  const when =
    span.from === span.to
      ? span.from === span.today
        ? "So far today"
        : `On ${DateTime.fromISO(span.from, { zone: tz }).toFormat("cccc d LLLL")}`
      : `From ${DateTime.fromISO(span.from, { zone: tz }).toFormat("d LLL")} to ${DateTime.fromISO(span.to, { zone: tz }).toFormat("d LLL")}`;
  if (!h || h.totals.bags === 0) return `${when} the bag system has reported no bags at ${place}.`;
  const t = h.totals;
  let s = `${when} ${t.bags.toLocaleString()} bags were counted on ${t.flights} ${t.flights === 1 ? "flight" : "flights"} at ${place}`;
  s += t.perFlight != null ? `, ${t.perFlight} a flight.` : ".";
  const dest = h.top.destinations[0];
  if (dest) s += ` ${dest.label} took the most checked bags (${dest.bags.toLocaleString()}, ${dest.share}%).`;
  if (t.connectingPct != null) s += ` ${t.connectingPct}% of bags connected between flights.`;
  if (t.perPax != null) s += ` That is ${t.perPax} bags a passenger.`;
  return s;
}

export function BagsTab({ from, to, today, tz, config }: { from: string; to: string; today: string; tz: string; config: AirportConfig | undefined }) {
  const live = to >= today;
  const q = useQuery({
    queryKey: ["bags-history", from, to],
    queryFn: () => api.bagsHistory(from, to),
    // Today's figures move while bags are being worked.
    refetchInterval: live ? 60_000 : false,
  });
  const place = config?.iata ?? "the airport";
  if (q.isLoading) return <Loader2 className="size-5 animate-spin text-ink-3" />;
  if (q.isError) return <p className="text-[13px] text-bad">{(q.error as Error).message}</p>;
  const h = q.data ?? null;
  const single = from === to;

  return (
    <>
      <HowToRead id="history.bags">
        <BagsNotes />
      </HowToRead>
      <p className="mb-4 max-w-3xl text-[14px] text-ink">{bagsSentence(h, place, { from, to, today }, tz)}</p>
      {h && h.totals.bags > 0 && (
        <>
          <Spotlight h={h} tz={tz} />
          <BagCards h={h} />

          <Section
            title="When are bags checked in?"
            caption={`Bags first counted at check-in in each clock hour${single ? "" : `, averaged over the ${h.daysWithBags} days with bags`}. Read from the bag system's running counts as they rose.`}
          >
            <HourBars byHour={h.byHour} days={single ? 1 : Math.max(1, h.daysWithBags)} />
          </Section>

          <Section
            title="Where are the bags going in the end?"
            caption="Bags by the airport they are tagged to, from the bag system's final destinations. Direct bags end where the flight goes; connecting bags go on from there, through the airport in the last list."
          >
            {h.destinationsReported === 0 && <NoDestinations h={h} />}
            <div className="grid gap-3 lg:grid-cols-3">
              <RankList title="Top final destinations" items={h.top.finalDestinations} empty={NO_FINALS} />
              <RankList title="Top connecting destinations" items={h.top.connectingDestinations} empty={NO_FINALS} />
              <RankList title="Connecting through" items={h.top.connectingVia} empty={NO_FINALS} />
            </div>
          </Section>

          <Section
            title="Where do the bags go, and whose are they?"
            caption="Departures' bags by the airport the flight goes to, arrivals' by where it came from, and every flight's by airline. Each bar is the share of that list's bags."
          >
            <div className="grid gap-3 lg:grid-cols-3">
              <RankList title="Top destinations" items={h.top.destinations} empty="No departures with bags in the flight list." />
              <RankList title="Top origins" items={h.top.origins} empty="No arrivals with bags in the flight list." />
              <RankList title="Top airlines" items={h.top.airlines} empty="No airline found." />
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              <RankList title="Busiest flights" items={h.top.flights} empty="No flights." />
              {h.top.connections.length > 0 && <RankList title="Changing flights here" items={h.top.connections} empty="" />}
            </div>
          </Section>

          {!single && (
            <Section title="Day by day" caption="Each day in the range with bags, newest first. Connecting and direct are over the flights that said which were which.">
              <DayTable h={h} tz={tz} />
            </Section>
          )}

          <Section
            title="Bags by flight"
            caption={`One row per flight number, most bags first${single ? "" : ", added up over the range with the average per flight flown"}. Bags per passenger uses the airline's passenger figure, or the security count for a departure without one.`}
          >
            <FlightRows rows={h.flights} config={config} single={single} />
          </Section>
        </>
      )}
    </>
  );
}

export function BagsNotes() {
  return (
    <>
      <li>Every figure comes from the bag system (VectroLake). A flight&apos;s bags are the count at the furthest point that reported: loaded if it was, otherwise make-up, screening or check-in.</li>
      <li>
        A connecting bag goes on past its flight: on a departure, tagged beyond where the flight goes (to YYC, on to London); on an arrival, on to another flight here. Direct bags end
        where the flight goes, or are claimed here.
      </li>
      <li>
        Direct and connecting are worked out from the bag system&apos;s final destination for each bag, or a connecting count, only on flights that sent one. Until then they read
        &quot;{NOT_REPORTED}&quot;, never zero.
      </li>
      <li>Bags per passenger divides bags by passengers on the flights that have both figures: the airline&apos;s passenger count where it sent one, the security count for a departure where it did not.</li>
      <li>Destinations and airlines come from the flight list. A count for a flight not in it is still added to the totals, but it has no destination.</li>
      <li>The page opens on today and refreshes every minute while today is in the range.</li>
    </>
  );
}

// ------------------------------------------------------------------ the spotlight

interface Highlight {
  label: string;
  value: string;
  sub: string;
  list: BagRanked[];
}

/** What the strip at the top cycles through: each list's leader, with the list behind it. */
export function highlights(h: BagsHistory, tz: string): Highlight[] {
  const out: Highlight[] = [];
  const lead = (label: string, list: BagRanked[], unit: (r: BagRanked) => string) => {
    const r = list[0];
    if (r) out.push({ label, value: r.label, sub: unit(r), list });
  };
  const bagsOf = (r: BagRanked) => `${r.bags.toLocaleString()} bags · ${r.share}% · ${r.flights} ${r.flights === 1 ? "flight" : "flights"}`;
  lead("Top final destination", h.top.finalDestinations, bagsOf);
  lead("Top connecting destination", h.top.connectingDestinations, bagsOf);
  lead("Top destination", h.top.destinations, bagsOf);
  lead("Top connecting hub", h.top.connectingVia, bagsOf);
  lead("Top transfer here", h.top.connections, bagsOf);
  lead("Top origin", h.top.origins, bagsOf);
  lead("Top airline", h.top.airlines, bagsOf);
  lead("Busiest flight", h.top.flights, (r) => `${r.bags.toLocaleString()} bags${r.flights > 1 ? ` over ${r.flights} flights` : ""}`);
  const peak = h.byHour.reduce((best, v, i) => (v > (h.byHour[best] ?? 0) ? i : best), 0);
  if ((h.byHour[peak] ?? 0) > 0) {
    out.push({
      label: "Busiest check-in hour",
      value: `${hh(peak)}–${hh((peak + 1) % 24)}`,
      sub: `${h.byHour[peak]!.toLocaleString()} bags checked in`,
      list: [],
    });
  }
  if (h.lastAt) {
    const at = DateTime.fromISO(h.lastAt, { zone: tz });
    out.push({ label: "Last report", value: at.toFormat("HH:mm"), sub: at.toFormat("cccc d LLL"), list: [] });
  }
  return out;
}

const ROTATE_MS = 5000;

function Spotlight({ h, tz }: { h: BagsHistory; tz: string }) {
  const items = useMemo(() => highlights(h, tz), [h, tz]);
  const [i, setI] = useState(0);
  const [held, setHeld] = useState(false);
  const still = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    if (held || still || items.length < 2) return;
    const t = setInterval(() => setI((n) => (n + 1) % items.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [held, still, items.length]);
  if (!items.length) return null;
  const at = items[i % items.length]!;
  const best = at.list[0]?.bags ?? 0;
  return (
    <div className={clsx(card, "mb-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]")} onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)} aria-live="polite">
      <div className="flex min-w-0 flex-col justify-between gap-3">
        <div key={i} className="spot-in">
          <div className="text-[11px] font-semibold tracking-wide text-brand uppercase">{at.label}</div>
          <div className="tabular truncate text-[30px] leading-tight font-bold">{at.value}</div>
          <div className="text-[12.5px] text-ink-2">{at.sub}</div>
        </div>
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Highlights">
          {items.map((x, n) => (
            <button
              key={x.label}
              role="tab"
              aria-selected={n === i % items.length}
              onClick={() => setI(n)}
              className={clsx("rounded-full px-2 py-0.5 text-[10.5px] font-semibold", n === i % items.length ? "bg-brand text-brand-ink" : "bg-panel-2 text-ink-3 hover:text-ink")}
            >
              {x.label}
            </button>
          ))}
        </div>
      </div>
      {at.list.length > 0 && (
        <ol className="flex flex-col justify-center gap-1">
          {at.list.slice(0, 5).map((r, n) => (
            <li key={r.key} className="grid grid-cols-[1.25rem_minmax(0,9rem)_minmax(0,1fr)_3.5rem] items-center gap-2 text-[12px]">
              <span className="tabular text-ink-3">{n + 1}</span>
              <span className="truncate font-semibold">{r.label}</span>
              <span className="h-2 rounded-full bg-panel-2">
                <span className="block h-full rounded-full bg-brand transition-[width] duration-500" style={{ width: `${best ? (r.bags / best) * 100 : 0}%` }} />
              </span>
              <span className="tabular text-right text-ink-2">{r.bags.toLocaleString()}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ figures

function BagCards({ h }: { h: BagsHistory }) {
  const t = h.totals;
  const told = t.connectingReported;
  const toldSub = told ? `over ${told} of ${t.flights} flights that said` : "the bag system does not send it yet";
  return (
    <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
      <Card label="Bags" value={num(t.bags)} sub={`${num(t.departures)} out · ${num(t.arrivals)} in`} />
      <Card label="Per flight" value={num(t.perFlight)} sub={`${t.flights} ${t.flights === 1 ? "flight" : "flights"} with bags`} />
      <Card label="Direct" value={t.direct == null ? NOT_REPORTED : num(t.direct)} sub={toldSub} muted={t.direct == null} />
      <Card label="Connecting" value={t.connecting == null ? NOT_REPORTED : num(t.connecting)} sub={toldSub} muted={t.connecting == null} />
      <Card label="Connecting share" value={t.connectingPct == null ? NOT_REPORTED : `${t.connectingPct}%`} sub="of bags on flights that said" muted={t.connectingPct == null} />
      <Card
        label="Bags per passenger"
        value={t.perPax == null ? "—" : t.perPax.toFixed(2)}
        sub={t.perPax == null ? "no passenger figures for these flights" : `${num(t.paxBasis.bags)} bags, ${num(t.paxBasis.pax)} passengers, ${t.paxBasis.flights} flights`}
      />
      <Card label="Days with bags" value={num(h.daysWithBags)} sub={t.unmatched ? `${t.unmatched} flight-days not in the flight list` : "every flight matched"} />
    </div>
  );
}

function Card({ label, value, sub, muted }: { label: string; value: string; sub?: string; muted?: boolean }) {
  return (
    <div className="rounded-xl border border-line p-3">
      <div className="text-[11px] tracking-wide text-ink-3 uppercase">{label}</div>
      <div className={clsx("tabular font-bold", muted ? "text-[14px] leading-[30px] text-ink-3" : "text-[20px]")}>{value}</div>
      {sub && <div className="text-[11px] text-ink-3">{sub}</div>}
    </div>
  );
}

function HourBars({ byHour, days }: { byHour: number[]; days: number }) {
  const values = byHour.map((v) => v / days);
  const max = Math.max(...values, 1);
  if (values.every((v) => v === 0)) return <p className="text-[12.5px] text-ink-3">No check-in counts with times in this range.</p>;
  return (
    <div className={card}>
      <div className="flex h-32 items-end gap-[3px]">
        {values.map((v, h) => (
          <div key={h} className="flex h-full flex-1 flex-col justify-end" title={`${hh(h)}–${hh((h + 1) % 24)}: ${Math.round(v).toLocaleString()} bags${days > 1 ? " a day" : ""}`}>
            <div className="rounded-t-[3px] bg-info" style={{ height: `${(v / max) * 100}%`, minHeight: v > 0 ? 2 : 0 }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-[3px] text-[9.5px] text-ink-3">
        {values.map((_, h) => (
          <span key={h} className="flex-1 text-center">
            {h % 3 === 0 ? String(h).padStart(2, "0") : ""}
          </span>
        ))}
      </div>
    </div>
  );
}

function RankList({ title, items, empty }: { title: string; items: BagRanked[]; empty: string }) {
  const best = items[0]?.bags ?? 0;
  return (
    <div className={card}>
      <div className="mb-2 text-[12px] font-semibold">{title}</div>
      {items.length === 0 ? (
        <p className="text-[12px] text-ink-3">{empty}</p>
      ) : (
        <ol className="flex flex-col gap-1">
          {items.slice(0, 8).map((r) => (
            <li key={r.key} className="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)_3.5rem_2.75rem] items-center gap-2 text-[12px]" title={`${r.label}: ${r.bags} bags over ${r.flights} flights`}>
              <span className="truncate">{r.label}</span>
              <span className="h-1.5 rounded-full bg-panel-2">
                <span className="block h-full rounded-full bg-info" style={{ width: `${best ? (r.bags / best) * 100 : 0}%` }} />
              </span>
              <span className="tabular text-right">{r.bags.toLocaleString()}</span>
              <span className="tabular text-right text-ink-3">{r.share}%</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function DayTable({ h, tz }: { h: BagsHistory; tz: string }) {
  return (
    <div className="scroll-thin overflow-x-auto">
      <table className="w-full max-w-5xl text-[12.5px]">
        <thead>
          <tr className="border-b border-line">
            <th className={th}>Day</th>
            <th className={th}>Bags</th>
            <th className={th}>Flights</th>
            <th className={th}>Per flight</th>
            <th className={th}>Out</th>
            <th className={th}>In</th>
            <th className={th}>Direct</th>
            <th className={th}>Connecting</th>
            <th className={th}>% connecting</th>
            <th className={th}>Per passenger</th>
          </tr>
        </thead>
        <tbody>
          {h.days.map((d) => (
            <tr key={d.date} className="tabular border-b border-line">
              <td className={clsx(td, "font-semibold")}>{DateTime.fromISO(d.date, { zone: tz }).toFormat("ccc d LLL")}</td>
              <td className={td}>{num(d.bags)}</td>
              <td className={td}>{d.flights}</td>
              <td className={td}>{num(d.perFlight)}</td>
              <td className={td}>{num(d.departures)}</td>
              <td className={td}>{num(d.arrivals)}</td>
              <td className={clsx(td, d.direct == null && "text-[11px] text-ink-3")}>{d.direct == null ? NOT_REPORTED : num(d.direct)}</td>
              <td className={clsx(td, d.connecting == null && "text-[11px] text-ink-3")}>{d.connecting == null ? NOT_REPORTED : num(d.connecting)}</td>
              <td className={clsx(td, d.connectingPct == null && "text-[11px] text-ink-3")}>{d.connectingPct == null ? NOT_REPORTED : `${d.connectingPct}%`}</td>
              <td className={td}>{d.perPax == null ? "—" : d.perPax.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FlightRows({ rows, config, single }: { rows: BagsFlightRow[]; config: AirportConfig | undefined; single: boolean }) {
  const [dir, setDir] = useState<"" | "ARR" | "DEP">("");
  const shown = rows.filter((r) => !dir || r.direction === dir);
  return (
    <>
      <div className="mb-2 flex gap-1 text-[12px]">
        {(["", "DEP", "ARR"] as const).map((d) => (
          <button key={d} onClick={() => setDir(d)} className={clsx("rounded-md px-2 py-0.5 font-semibold", dir === d ? "bg-panel-2 text-ink" : "text-ink-3 hover:text-ink")}>
            {d === "" ? "All" : d === "DEP" ? "Departures" : "Arrivals"}
          </button>
        ))}
      </div>
      <div className="scroll-thin overflow-x-auto">
        <table className="w-full max-w-5xl text-[12.5px]">
          <thead>
            <tr className="border-b border-line">
              <th className={th}>Flight</th>
              <th className={th}>To / from</th>
              {!single && <th className={th}>Flown</th>}
              <th className={th}>Bags</th>
              {!single && <th className={th}>Per flight</th>}
              <th className={th}>Connecting</th>
              <th className={th}>Passengers</th>
              <th className={th}>Per passenger</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={`${r.flight}|${r.direction}`} className="tabular border-b border-line">
                <td className={td}>
                  <span className="flex items-center gap-1.5">
                    <AirlineMark code={r.airline} config={config} />
                    <span className="font-semibold">{r.flight}</span>
                    <Direction dir={r.direction} />
                  </span>
                </td>
                <td className={clsx(td, "text-ink-2")}>{r.airport ?? "—"}</td>
                {!single && <td className={td}>{r.operations}</td>}
                <td className={td}>{num(r.bags)}</td>
                {!single && <td className={td}>{num(r.perOperation)}</td>}
                <td className={clsx(td, r.connecting == null && "text-ink-3")}>
                  {r.connecting == null ? "—" : `${num(r.connecting)}${r.connectingPct != null ? ` (${r.connectingPct}%)` : ""}`}
                </td>
                <td className={td}>{num(r.pax)}</td>
                <td className={td}>{r.perPax == null ? "—" : r.perPax.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
